import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight, Clapperboard, ImagePlus, Loader2, Plus, RectangleVertical, RotateCcw, Send, Sparkles, Trash2 } from 'lucide-react';
import { FacebookIcon, InstagramIcon, LinkedInIcon } from './PlatformIcon';
import toast from 'react-hot-toast';
import { useBusiness } from '../contexts/BusinessContext';
import { SOCIAL_ENABLED } from '../lib/featureFlags';
import { supabase } from '../lib/supabaseClient';
import { invokeErrorMessage } from '../lib/supabaseFunctionError';
import type { MediaItem } from '../lib/mediaLibrary';
import {
  deleteMedia,
  isSocialComposeMediaSource,
  mediaPreviewUrl,
  mediaThumbUrl,
  uploadMedia,
} from '../lib/mediaLibrary';
import MediaLibraryPicker from './MediaLibraryPicker';
import type { SocialPlatform } from './SocialConnectButton';
import BulkCreateSocialPostsModal from './marketing/BulkCreateSocialPostsModal';
import CrossPostToggle from './marketing/CrossPostToggle';
import { publishSocialTwin, type CrossPostPlatform } from '../lib/socialCrossPost';
import InstagramAiCreatePanel from './marketing/InstagramAiCreatePanel';
import InstagramPostExtras, {
  toInstagramOptionsPayload,
  type InstagramComposeOptions,
  type InstagramExtrasSection,
} from './marketing/InstagramPostExtras';
import SocialImageCropEditor, {
  IG_ASPECT_OPTIONS,
  IG_ASPECT_RATIOS,
  bakeCroppedImage,
  defaultSocialCropState,
  type IgAspectKey,
  type SocialCropState,
} from './marketing/SocialImageCropEditor';
import {
  captionStyle,
  defaultVideoCaption,
  hasCaptionText,
  VIDEO_CAPTION_COLORS,
  VIDEO_CAPTION_MAX_CHARS,
  VIDEO_CAPTION_STYLES,
  type VideoCaption,
} from '../lib/videoCaption';
import {
  checkSocialFormat,
  FORMAT_IMAGE_ASPECT,
  SOCIAL_FORMAT_SPECS,
  SOCIAL_POST_FORMATS,
  type SocialPostFormat,
} from '../lib/socialPostFormat';
import { isVideoMedia } from '../lib/mediaLibrary';
import { bakeCroppedVideo, captureVideoPoster, renderVideoCoverJpeg, videoOutputSize } from '../lib/bakeCroppedVideo';
import VideoCoverControls, { type VideoCoverChoice } from './marketing/VideoCoverControls';

/** One edit round in a slide's AI history: the prompt and the resulting image. */
export type AiEditTurn = {
  id: string;
  /** The user's edit prompt. */
  label: string;
  status: 'pending' | 'done' | 'error';
  /** Resulting image (set when status is 'done') — restorable version. */
  media?: MediaItem;
  /** Why the edit failed (set when status is 'error'). */
  errorMessage?: string;
};

/** JPEG blob → raw base64 (no data-URL prefix) for the edit function. */
async function blobToBase64(blob: Blob): Promise<string> {
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Failed to encode image for AI edit'));
    reader.readAsDataURL(blob);
  });
  const base64 = dataUrl.split(',')[1];
  if (!base64 || base64.length < 100) throw new Error('Failed to encode image for AI edit');
  return base64;
}

export type InstagramSlide = {
  key: string;
  media: MediaItem;
  crop: SocialCropState;
  /** Set for video slides. Framing is baked in on publish. */
  isVideo?: boolean;
  /** Text drawn onto the video (not the post caption). */
  videoCaption?: VideoCaption;
  /** JPEG data URL of a frame — thumbnails and AI context for video slides. */
  posterUrl?: string;
  /**
   * Cover sent to the network instead of the first frame.
   * Absent means the network picks the opening frame.
   */
  videoCover?: VideoCoverChoice;
  /** When this slide came from a Google review card. */
  sourceReviewId?: string;
  /**
   * Original Media Library asset this slide was derived from.
   * Survives AI edit / crop so we can mark the library photo as used.
   */
  sourceLibraryMediaId?: string;
  /** First photo before AI edits — undo restores this. */
  baselineMedia?: MediaItem;
  /** Chat-style refine turns for this slide. */
  aiEditTurns?: AiEditTurn[];
};

/** Re-encode only when the posted frame or a burned-in caption differs from the file. */
function videoSlideNeedsBake(slide: InstagramSlide, aspect: number): boolean {
  if (hasCaptionText(slide.videoCaption)) return true;
  const moved =
    slide.crop.zoom > 1.02 ||
    Math.abs(slide.crop.crop.x) > 2 ||
    Math.abs(slide.crop.crop.y) > 2;
  if (moved) return true;
  const { width, height } = slide.media;
  if (!width || !height || aspect <= 0) return true;
  return Math.abs(width / height - aspect) / aspect > 0.04;
}

function librarySourceIdForMedia(media: MediaItem, existing?: string): string | undefined {
  if (existing) return existing;
  if (!media?.id || isSocialComposeMediaSource(media.source)) return undefined;
  return media.id;
}

const PLATFORM_META: Record<
  SocialPlatform,
  {
    label: string;
    placeholder: string;
    functionName: string;
    requireImage: boolean;
    maxImages: number;
    maxLength: number;
    Icon: typeof FacebookIcon;
    publishLabel: string;
  }
> = {
  facebook: {
    label: 'Facebook',
    placeholder: 'Write your Facebook post…',
    functionName: 'ayrshare-facebook-posts',
    requireImage: false,
    maxImages: 10,
    maxLength: 2200,
    Icon: FacebookIcon,
    publishLabel: 'Publish to Facebook',
  },
  instagram: {
    label: 'Instagram',
    placeholder: 'Write your Instagram caption…',
    functionName: 'ayrshare-instagram-posts',
    requireImage: true,
    maxImages: 10,
    maxLength: 2200,
    Icon: InstagramIcon,
    publishLabel: 'Publish to Instagram',
  },
  linkedin: {
    label: 'LinkedIn',
    placeholder: 'Write your LinkedIn post…',
    functionName: 'ayrshare-linkedin-posts',
    requireImage: false,
    maxImages: 9,
    maxLength: 3000,
    Icon: LinkedInIcon,
    publishLabel: 'Publish to LinkedIn',
  },
};

/** Composed social post returned by collect mode (multi-media campaign wizard). */
export interface SocialPostCollectDraft {
  platform: SocialPlatform;
  connectionId: string;
  displayName: string | null;
  content: string;
  /** Raw compose state for re-editing. */
  slides: InstagramSlide[];
  aspectKey: IgAspectKey;
  postFormat?: SocialPostFormat;
  instagramOptions: InstagramComposeOptions;
  /** Baked publish payload, ready for finalize. */
  mediaIds: string[];
  mediaUrls: string[];
  googleReviewIds: string[];
  sourceLibraryMediaIds: string[];
  instagramOptionsPayload: Record<string, unknown> | null;
  /** Public JPEG used as the video cover. Null when the network should use the first frame. */
  thumbnailUrl?: string | null;
  /** Same caption and photos, posted to the other connected Facebook or Instagram account. */
  crossPost?: { platform: CrossPostPlatform; connectionId: string } | null;
}

export interface CreateSocialPostModalProps {
  open: boolean;
  onClose: () => void;
  platform: SocialPlatform;
  /** Called after a successful publish or stage. */
  onSuccess?: () => void;
  /** Prefer this connection when resolving the active social account. */
  connectionId?: string | null;
  subtitle?: string;
  /** When set, open in "Stage for later" with this datetime-local value (YYYY-MM-DDTHH:mm). */
  initialScheduleLocal?: string | null;
  /**
   * Collect mode: don't publish/stage — bake the images, then hand the composed
   * post back to the caller (multi-media campaign wizard). Hides the now/schedule picker.
   */
  collect?: {
    initialDraft?: SocialPostCollectDraft | null;
    onCollect: (draft: SocialPostCollectDraft) => void;
  } | null;
  /** Hide the Facebook/Instagram copy switch (both channels are already in this flow). */
  hideCrossPost?: boolean;
}

/** One restorable image version in a slide's AI edit history. */
function AiVersionRow({
  media,
  label,
  isCurrent,
  disabled,
  onRestore,
}: {
  media: MediaItem;
  label: string;
  isCurrent: boolean;
  disabled: boolean;
  onRestore: () => void;
}) {
  return (
    <div className="flex items-center gap-3">
      <img
        src={mediaThumbUrl(media.url)}
        alt=""
        className="h-16 w-16 rounded-lg object-cover border border-gray-200 bg-white shrink-0"
      />
      <p className="min-w-0 flex-1 text-xs font-semibold text-gray-700">{label}</p>
      {isCurrent ? (
        <span className="shrink-0 rounded-full bg-blue-600 px-2.5 py-1 text-[11px] font-semibold text-white">
          Current
        </span>
      ) : (
        <button
          type="button"
          onClick={onRestore}
          disabled={disabled}
          className="shrink-0 inline-flex items-center gap-1 rounded-full border border-gray-300 bg-white px-2.5 py-1 text-[11px] font-semibold text-gray-700 hover:bg-gray-50 active:scale-95 transition disabled:opacity-45"
          style={{ WebkitTapHighlightColor: 'transparent' }}
        >
          <RotateCcw className="h-3 w-3" />
          Restore
        </button>
      )}
    </div>
  );
}

function formatScheduleShort(local: string): string | null {
  if (!local) return null;
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function browserTimezoneLabel(): string {
  let tz = '';
  try {
    tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    tz = '';
  }
  const mins = -new Date().getTimezoneOffset();
  const sign = mins >= 0 ? '+' : '-';
  const abs = Math.abs(mins);
  const h = Math.trunc(abs / 60);
  const m = abs % 60;
  const gmt = `GMT${sign}${h}${m ? `:${String(m).padStart(2, '0')}` : ''}`;
  return tz ? `${tz.replace(/_/g, ' ')} (${gmt})` : gmt;
}

/**
 * Shared Facebook / Instagram create-post modal (Media Manager, Control Centre, etc.).
 * Two-step flow: 1) media editor (crop, aspect, carousel, AI edits) → 2) details
 * (caption, IG extras, schedule, stage/publish).
 */
export default function CreateSocialPostModal({
  open,
  onClose,
  platform,
  onSuccess,
  connectionId: preferredConnectionId = null,
  initialScheduleLocal = null,
  collect = null,
  hideCrossPost = false,
}: CreateSocialPostModalProps) {
  const meta = PLATFORM_META[platform];
  const otherPlatform: CrossPostPlatform | null =
    platform === 'facebook' ? 'instagram' : platform === 'instagram' ? 'facebook' : null;
  const otherMeta = otherPlatform ? PLATFORM_META[otherPlatform] : null;
  const PlatformIcon = meta.Icon;
  const isInstagram = platform === 'instagram';
  const { selectedBusiness } = useBusiness();
  const businessId = selectedBusiness?.id;

  const [isAnimating, setIsAnimating] = useState(false);
  const [resolving, setResolving] = useState(true);
  const [connectionId, setConnectionId] = useState<string | null>(null);
  const [otherConnectionId, setOtherConnectionId] = useState<string | null>(null);
  const [alsoPostToOther, setAlsoPostToOther] = useState(false);
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [step, setStep] = useState<'media' | 'details'>('media');
  const [postContent, setPostContent] = useState('');
  const [postMode, setPostMode] = useState<'now' | 'schedule'>('now');
  const [scheduleLocal, setScheduleLocal] = useState('');
  const [showScheduleEditor, setShowScheduleEditor] = useState(false);
  const [igSection, setIgSection] = useState<InstagramExtrasSection | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [slides, setSlides] = useState<InstagramSlide[]>([]);
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const [aspectKey, setAspectKey] = useState<IgAspectKey>('4:5');
  const [postFormat, setPostFormat] = useState<SocialPostFormat>('feed');
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [showCoverPicker, setShowCoverPicker] = useState(false);
  const [showAddPhotoMenu, setShowAddPhotoMenu] = useState(false);
  const [showAiCreate, setShowAiCreate] = useState(false);
  const [writingCaption, setWritingCaption] = useState(false);
  const [showCaptionAi, setShowCaptionAi] = useState(false);
  const [captionAiMode, setCaptionAiMode] = useState<'menu' | 'prompt'>('menu');
  const [captionPrompt, setCaptionPrompt] = useState('');
  const [aiEditPrompt, setAiEditPrompt] = useState('');
  const [editingImage, setEditingImage] = useState(false);
  const [instagramOptions, setInstagramOptions] = useState<InstagramComposeOptions>({});
  const [showBulkCreate, setShowBulkCreate] = useState(false);

  /** Media created in this compose session (AI / crops / review cards) — discarded on abandon. */
  const ephemeralMediaIdsRef = useRef<Set<string>>(new Set());
  const sessionCommittedRef = useRef(false);

  const trackEphemeralMedia = (id: string) => {
    if (id) ephemeralMediaIdsRef.current.add(id);
  };

  const softDeleteEphemeralIds = async (ids: string[]) => {
    await Promise.all(ids.map((id) => deleteMedia(id).catch(() => undefined)));
  };

  /** Soft-delete session-created media except ids we want to keep (published/staged). */
  const discardUncommittedEphemeral = async (keepIds: string[] = []) => {
    const keep = new Set(keepIds);
    const toDelete = [...ephemeralMediaIdsRef.current].filter((id) => !keep.has(id));
    for (const id of toDelete) ephemeralMediaIdsRef.current.delete(id);
    if (toDelete.length > 0) await softDeleteEphemeralIds(toDelete);
  };

  useEffect(() => {
    if (open) {
      setIsAnimating(true);
      sessionCommittedRef.current = false;
      ephemeralMediaIdsRef.current = new Set();
    }
  }, [open]);

  useEffect(() => {
    if (!open || !SOCIAL_ENABLED || !businessId) {
      if (open) setResolving(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setResolving(true);
      try {
        let query = supabase
          .from('social_media_connections')
          .select('id, display_name, username')
          .eq('business_id', businessId)
          .eq('platform', platform)
          .eq('provider', 'ayrshare')
          .eq('is_active', true)
          .limit(1);
        if (preferredConnectionId) {
          query = query.eq('id', preferredConnectionId);
        }
        const { data } = await query.maybeSingle();
        if (cancelled) return;
        if (data) {
          setConnectionId(data.id as string);
          setDisplayName(
            (data.display_name as string) ||
              (data.username as string) ||
              meta.label,
          );
        } else if (preferredConnectionId) {
          const { data: fallback } = await supabase
            .from('social_media_connections')
            .select('id, display_name, username')
            .eq('business_id', businessId)
            .eq('platform', platform)
            .eq('provider', 'ayrshare')
            .eq('is_active', true)
            .limit(1)
            .maybeSingle();
          if (cancelled) return;
          setConnectionId((fallback?.id as string) || null);
          setDisplayName(
            (fallback?.display_name as string) ||
              (fallback?.username as string) ||
              null,
          );
        } else {
          setConnectionId(null);
          setDisplayName(null);
        }

        if (otherPlatform && !hideCrossPost) {
          const { data: other } = await supabase
            .from('social_media_connections')
            .select('id')
            .eq('business_id', businessId)
            .eq('platform', otherPlatform)
            .eq('provider', 'ayrshare')
            .eq('is_active', true)
            .limit(1)
            .maybeSingle();
          if (cancelled) return;
          setOtherConnectionId((other?.id as string) || null);
        } else if (!cancelled) {
          setOtherConnectionId(null);
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setConnectionId(null);
          setDisplayName(null);
          setOtherConnectionId(null);
        }
      } finally {
        if (!cancelled) setResolving(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, businessId, platform, preferredConnectionId, meta.label, hideCrossPost, otherPlatform]);

  useEffect(() => {
    if (!open) {
      setStep('media');
      setPostContent('');
      setPostMode('now');
      setScheduleLocal('');
      setShowScheduleEditor(false);
      setIgSection(null);
      setPublishing(false);
      setSlides([]);
      setActiveSlideIndex(0);
      setAspectKey('4:5');
      setPostFormat('feed');
      setShowMediaPicker(false);
      setShowCoverPicker(false);
      setShowAddPhotoMenu(false);
      setShowAiCreate(false);
      setWritingCaption(false);
      setShowCaptionAi(false);
      setCaptionAiMode('menu');
      setCaptionPrompt('');
      setAiEditPrompt('');
      setEditingImage(false);
      setInstagramOptions({});
      setAlsoPostToOther(false);
      setOtherConnectionId(null);
      return;
    }
    setStep('media');
    if (initialScheduleLocal) {
      setPostMode('schedule');
      setScheduleLocal(initialScheduleLocal);
    } else {
      setPostMode('now');
      setScheduleLocal('');
    }
    // Collect mode re-edit: restore the previously composed post.
    const draft = collect?.initialDraft;
    if (draft) {
      setPostContent(draft.content);
      setSlides(draft.slides);
      setActiveSlideIndex(0);
      setAspectKey(draft.aspectKey);
      setPostFormat(draft.postFormat || 'feed');
      setInstagramOptions(draft.instagramOptions);
      setAlsoPostToOther(!!draft.crossPost);
    } else {
      setAlsoPostToOther(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialScheduleLocal, collect?.initialDraft]);

  // Reels and Stories are full-screen 9:16; feed posts use the chosen ratio.
  const aspect =
    postFormat === 'feed' ? IG_ASPECT_RATIOS[aspectKey] : FORMAT_IMAGE_ASPECT[postFormat];
  const activeSlide = slides[activeSlideIndex] || null;
  const hasVideoSlide = slides.some((s) => s.isVideo);
  const supportsFormat = platform === 'instagram' || platform === 'facebook';
  const [captionSelected, setCaptionSelected] = useState(false);

  /** Thumbnail for a slide: still frame for videos, CDN thumb for photos. */
  const slideThumbUrl = (slide: InstagramSlide): string | null =>
    slide.isVideo ? slide.posterUrl || null : mediaThumbUrl(slide.media.url);

  // Grab a poster frame for any video slide that doesn't have one yet.
  const posterPendingRef = useRef(new Set<string>());
  useEffect(() => {
    for (const slide of slides) {
      if (!slide.isVideo || slide.posterUrl || posterPendingRef.current.has(slide.media.id)) continue;
      posterPendingRef.current.add(slide.media.id);
      const mediaId = slide.media.id;
      const url = slide.media.url;
      void captureVideoPoster(url)
        .then((posterUrl) => {
          setSlides((prev) =>
            prev.map((s) => (s.media.id === mediaId && !s.posterUrl ? { ...s, posterUrl } : s)),
          );
        })
        .catch((err) => {
          console.warn('Could not capture a video poster frame:', err);
        })
        .finally(() => {
          posterPendingRef.current.delete(mediaId);
        });
    }
  }, [slides]);

  const formatProblems = useMemo(() => {
    if (!supportsFormat || slides.length === 0) return [];
    return checkSocialFormat(
      platform,
      postFormat,
      slides.map((s) => ({
        kind: s.isVideo ? 'video' : 'image',
        aspect: s.media.width && s.media.height ? s.media.width / s.media.height : null,
        durationSeconds: s.isVideo ? s.media.duration_seconds ?? null : null,
      })),
    );
  }, [supportsFormat, slides, platform, postFormat]);
  const formatErrors = formatProblems.filter((p) => p.level === 'error');
  const formatSpec = supportsFormat ? SOCIAL_FORMAT_SPECS[platform][postFormat] : null;

  // Keep the refine prompt scoped to the active slide.
  useEffect(() => {
    setAiEditPrompt('');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset only on slide switch
  }, [activeSlideIndex]);

  // Keep the AI history log scrolled to the latest turn.
  const aiChatEndRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    aiChatEndRef.current?.scrollIntoView({ block: 'nearest' });
  }, [activeSlide?.aiEditTurns?.length, editingImage, activeSlideIndex]);

  const canPublish = useMemo(() => {
    if (!connectionId) return false;
    if (platform === 'linkedin') {
      if (!postContent.trim() && slides.length === 0) return false;
    } else if (!postContent.trim()) {
      return false;
    }
    if (meta.requireImage && slides.length === 0) return false;
    if (formatErrors.length > 0) return false;
    return true;
  }, [postContent, connectionId, meta.requireImage, slides.length, platform, formatErrors.length]);

  useEffect(() => {
    if (otherPlatform === 'instagram' && slides.length === 0 && alsoPostToOther) {
      setAlsoPostToOther(false);
    }
  }, [otherPlatform, slides.length, alsoPostToOther]);

  const canGoNext = useMemo(() => {
    if (!connectionId || resolving || publishing || editingImage) return false;
    if (meta.requireImage && slides.length === 0) return false;
    if (formatErrors.length > 0) return false;
    return true;
  }, [connectionId, resolving, publishing, editingImage, meta.requireImage, slides.length, formatErrors.length]);

  const handleTransitionEnd = () => {
    if (!open) setIsAnimating(false);
  };

  const requestClose = () => {
    if (publishing || writingCaption || editingImage) return;
    void (async () => {
      if (!sessionCommittedRef.current) {
        await discardUncommittedEphemeral();
      }
      ephemeralMediaIdsRef.current.clear();
      onClose();
    })();
  };

  const goToDetails = () => {
    if (!canGoNext) return;
    setShowAddPhotoMenu(false);
    setShowAiCreate(false);
    setStep('details');
  };

  const goToMedia = () => {
    if (publishing || writingCaption) return;
    setShowCaptionAi(false);
    setCaptionAiMode('menu');
    setStep('media');
  };

  const finishComposeSuccess = async (keepIds: string[]) => {
    sessionCommittedRef.current = true;
    await discardUncommittedEphemeral(keepIds);
    ephemeralMediaIdsRef.current.clear();
    onSuccess?.();
    onClose();
  };

  const handleAiEditImage = async (promptText: string) => {
    if (!businessId || !activeSlide || editingImage) return;
    const custom = String(promptText || '').trim();
    if (custom.length < 8) {
      toast.error('Describe the edit (a short sentence works)');
      return;
    }

    const turnId = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const turnLabel = custom;

    const failTurn = (message: string) => {
      setSlides((prev) =>
        prev.map((s, i) =>
          i === activeSlideIndex
            ? {
                ...s,
                aiEditTurns: (s.aiEditTurns || []).map((t) =>
                  t.id === turnId
                    ? { ...t, status: 'error' as const, errorMessage: message }
                    : t,
                ),
              }
            : s,
        ),
      );
    };

    setEditingImage(true);
    setAiEditPrompt('');
    setSlides((prev) =>
      prev.map((s, i) =>
        i === activeSlideIndex
          ? {
              ...s,
              baselineMedia: s.baselineMedia || s.media,
              aiEditTurns: [
                ...(s.aiEditTurns || []),
                { id: turnId, label: turnLabel, status: 'pending' as const },
              ],
            }
          : s,
      ),
    );

    try {
      // Bake exactly what's framed in the editor (crop + zoom + selected
      // aspect) and edit THAT, so the AI works on the composition the user
      // sees — e.g. a logo lands inside the final 1:1 frame instead of
      // somewhere on the uncropped photo.
      const baked = await bakeCroppedImage(
        mediaPreviewUrl(activeSlide.media.url),
        activeSlide.crop.croppedAreaPixels,
        { aspect, maxEdge: 1440, quality: 0.92 },
      );
      const imageBase64 = await blobToBase64(baked);
      const { data, error } = await supabase.functions.invoke('ai-edit-social-image', {
        body: {
          businessId,
          imageUrl: activeSlide.media.url,
          imageBase64,
          imageMime: 'image/jpeg',
          prompt: custom,
          // No size — 'auto' keeps the baked frame's aspect, so the result
          // comes back already composed for the selected Instagram ratio.
        },
      });
      if (error || data?.error) {
        const msg =
          data?.error ||
          (await invokeErrorMessage(error, data)) ||
          'Failed to edit image';
        console.error('[ai-edit-social-image]', { error, data, msg });
        const friendly =
          data?.code === 'insufficient_tokens' || /token/i.test(String(msg))
            ? 'Not enough AI tokens — purchase more in AI Settings'
            : String(msg).slice(0, 200);
        failTurn(friendly);
        toast.error(friendly);
        return;
      }
      const media = data?.media as MediaItem | undefined;
      if (!media?.id || !media?.url) {
        failTurn('AI edit did not return an image');
        toast.error('AI edit did not return an image');
        return;
      }
      trackEphemeralMedia(media.id);
      // Every version stays alive for the history log (restorable) until the
      // compose session ends — then uncommitted versions are soft-deleted.
      setSlides((prev) =>
        prev.map((s, i) =>
          i === activeSlideIndex
            ? {
                ...s,
                key: `${media.id}-${Date.now()}`,
                media,
                crop: defaultSocialCropState(),
                baselineMedia: s.baselineMedia || s.media,
                sourceLibraryMediaId: librarySourceIdForMedia(
                  s.media,
                  s.sourceLibraryMediaId,
                ),
                aiEditTurns: (s.aiEditTurns || []).map((t) =>
                  t.id === turnId ? { ...t, status: 'done' as const, media } : t,
                ),
              }
            : s,
        ),
      );
    } catch (err) {
      console.error(err);
      const msg = err instanceof Error ? err.message : 'Failed to edit image';
      failTurn(msg);
      toast.error(msg);
    } finally {
      setEditingImage(false);
    }
  };

  /** Make any version from the slide's history the current post image. */
  const restoreSlideVersion = (media: MediaItem) => {
    if (!activeSlide || editingImage || publishing) return;
    if (activeSlide.media.id === media.id) return;
    setSlides((prev) =>
      prev.map((s, i) =>
        i === activeSlideIndex
          ? {
              ...s,
              key: `${media.id}-restore-${Date.now()}`,
              media,
              crop: defaultSocialCropState(),
            }
          : s,
      ),
    );
  };

  const handleAspectChange = (next: IgAspectKey) => {
    setAspectKey(next);
    setSlides((prev) =>
      prev.map((s) => ({
        ...s,
        crop: defaultSocialCropState(),
      })),
    );
  };

  const openAddPhotoMenu = () => {
    if (publishing || editingImage || slides.length >= meta.maxImages) return;
    setShowAiCreate(false);
    setShowAddPhotoMenu((v) => !v);
  };

  const applyVideoFormat = (video: boolean) => {
    if (video && supportsFormat && postFormat === 'feed') setPostFormat('reel');
  };

  const addMediaAsSlide = (
    media: MediaItem,
    opts?: {
      suggestedCaption?: string;
      googleReviewId?: string;
      /** True for AI generate / review cards created in this session. */
      ephemeral?: boolean;
    },
  ) => {
    if (opts?.ephemeral) trackEphemeralMedia(media.id);
    applyVideoFormat(isVideoMedia(media));
    setSlides((prev) => {
      if (prev.length >= meta.maxImages) {
        toast.error(`Carousels support up to ${meta.maxImages} images`);
        return prev;
      }
      if (prev.some((s) => s.media.id === media.id)) {
        const idx = prev.findIndex((s) => s.media.id === media.id);
        if (idx >= 0) setActiveSlideIndex(idx);
        return prev;
      }
      const next: InstagramSlide = {
        key: `${media.id}-${Date.now()}`,
        media,
        crop: defaultSocialCropState(),
        isVideo: isVideoMedia(media),
        sourceReviewId: opts?.googleReviewId,
        sourceLibraryMediaId: librarySourceIdForMedia(media),
      };
      setActiveSlideIndex(prev.length);
      return [...prev, next];
    });
    if (opts?.suggestedCaption && !postContent.trim()) {
      setPostContent(opts.suggestedCaption);
    }
    setShowAiCreate(false);
    setShowAddPhotoMenu(false);
  };

  const slideGoogleReviewIds = () =>
    [...new Set(slides.map((s) => s.sourceReviewId).filter(Boolean))] as string[];

  const slideSourceLibraryMediaIds = () =>
    [
      ...new Set(
        slides
          .map((s) => s.sourceLibraryMediaId || librarySourceIdForMedia(s.media))
          .filter(Boolean),
      ),
    ] as string[];

  const handlePickerDone = (selected: MediaItem[]) => {
    if (selected.length > meta.maxImages) {
      toast.error(`Carousels support up to ${meta.maxImages} images`);
    }
    const nextItems = selected.slice(0, meta.maxImages);
    applyVideoFormat(nextItems.some((m) => isVideoMedia(m)));
    setSlides((prev) => {
      const byId = new Map(prev.map((s) => [s.media.id, s]));
      const bySource = new Map(
        prev
          .filter((s) => s.sourceLibraryMediaId)
          .map((s) => [s.sourceLibraryMediaId as string, s]),
      );
      const merged: InstagramSlide[] = nextItems.map((media) => {
        const existing = byId.get(media.id);
        if (existing) return existing;
        // Re-selecting a library original that was AI-edited: keep the edit slide.
        const fromSource = bySource.get(media.id);
        if (fromSource) return fromSource;
        return {
          key: `${media.id}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
          media,
          crop: defaultSocialCropState(),
          isVideo: isVideoMedia(media),
          sourceLibraryMediaId: librarySourceIdForMedia(media),
        };
      });
      setActiveSlideIndex((cur) => {
        if (merged.length === 0) return 0;
        const prevIds = new Set(prev.map((s) => s.media.id));
        const firstNew = merged.findIndex((s) => !prevIds.has(s.media.id));
        if (firstNew >= 0) return firstNew;
        return Math.min(cur, merged.length - 1);
      });
      return merged;
    });
    setShowMediaPicker(false);
  };

  const removeSlide = (index: number) => {
    setSlides((prev) => {
      const removed = prev[index];
      if (removed) {
        const ids = [
          removed.media.id,
          removed.baselineMedia?.id,
          ...(removed.aiEditTurns || []).map((t) => t.media?.id),
        ].filter(Boolean) as string[];
        for (const id of [...new Set(ids)]) {
          if (ephemeralMediaIdsRef.current.has(id)) {
            ephemeralMediaIdsRef.current.delete(id);
            void deleteMedia(id).catch(() => undefined);
          }
        }
      }
      const next = prev.filter((_, i) => i !== index);
      setActiveSlideIndex((cur) => {
        if (next.length === 0) return 0;
        if (cur >= next.length) return next.length - 1;
        if (cur > index) return cur - 1;
        return cur;
      });
      return next;
    });
  };

  const moveSlide = (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= slides.length) return;
    setSlides((prev) => {
      const next = [...prev];
      const [item] = next.splice(index, 1);
      next.splice(target, 0, item);
      return next;
    });
    setActiveSlideIndex(target);
  };

  const updateActiveCrop = (crop: SocialCropState) => {
    setSlides((prev) =>
      prev.map((s, i) => (i === activeSlideIndex ? { ...s, crop } : s)),
    );
  };

  const updateActiveVideoCaption = (patch: Partial<VideoCaption>) => {
    setSlides((prev) =>
      prev.map((s, i) =>
        i === activeSlideIndex
          ? { ...s, videoCaption: { ...(s.videoCaption || defaultVideoCaption()), ...patch } }
          : s,
      ),
    );
  };

  const updateActiveVideoCover = (videoCover: VideoCoverChoice | undefined) => {
    setSlides((prev) =>
      prev.map((s, i) => (i === activeSlideIndex ? { ...s, videoCover } : s)),
    );
  };

  const bakeAndUploadSlides = async (): Promise<{
    mediaUrls: string[];
    mediaIds: string[];
  }> => {
    if (!businessId) throw new Error('Missing business');
    if (slides.length === 0) throw new Error('Add at least one image');

    // Keep slide order. Videos are re-encoded only when the frame or a caption
    // differs from the original file — Meta has no crop-offset field.
    const ordered: Array<{ file?: File; existing?: MediaItem }> = [];
    let bakingVideo = false;
    try {
    for (let i = 0; i < slides.length; i++) {
      const slide = slides[i];
      if (slide.isVideo) {
        if (!videoSlideNeedsBake(slide, aspect)) {
          ordered.push({ existing: slide.media });
          continue;
        }
        if (!bakingVideo) {
          bakingVideo = true;
          toast.loading('Preparing video… 0%', { id: 'video-bake' });
        }
        const blob = await bakeCroppedVideo({
          url: slide.media.url,
          croppedAreaPixels: slide.crop.croppedAreaPixels,
          aspect,
          caption: slide.videoCaption,
          onProgress: (ratio) => {
            toast.loading(`Preparing video… ${Math.round(ratio * 100)}%`, { id: 'video-bake' });
          },
        });
        ordered.push({
          file: new File([blob], `ig-video-${i + 1}.mp4`, { type: 'video/mp4' }),
        });
        continue;
      }
      // Bake from the SAME preview-size URL the crop editor displays — the crop
      // pixel coords are in that image's space (and it's already CDN-cached).
      const blob = await bakeCroppedImage(
        mediaPreviewUrl(slide.media.url),
        slide.crop.croppedAreaPixels,
        {
          maxEdge: 1080,
          aspect,
        },
      );
      ordered.push({
        file: new File([blob], `ig-slide-${i + 1}.jpg`, { type: 'image/jpeg' }),
      });
    }

    const files = ordered.flatMap((item) => (item.file ? [item.file] : []));
    const uploaded = files.length > 0
      ? await uploadMedia(businessId, files, { source: 'social_crop' })
      : [];
    for (const m of uploaded) trackEphemeralMedia(m.id);
    let uploadIndex = 0;
    const all = ordered.map((item) => item.existing || uploaded[uploadIndex++]);
    return {
      mediaUrls: all.map((m) => m.url),
      mediaIds: all.map((m) => m.id),
    };
    } finally {
      if (bakingVideo) toast.dismiss('video-bake');
    }
  };

  /** JPEG cover for the video, uploaded to a public .jpg URL the networks can fetch. */
  const uploadVideoCover = async (): Promise<string | null> => {
    if (!businessId || postFormat === 'story') return null;
    const slide = slides.find((s) => s.isVideo && s.videoCover);
    if (!slide?.videoCover) return null;
    const bake = videoSlideNeedsBake(slide, aspect);
    const native =
      slide.media.width && slide.media.height
        ? { width: slide.media.width, height: slide.media.height }
        : null;
    const blob = await renderVideoCoverJpeg({
      aspect,
      outputSize: bake ? videoOutputSize(aspect) : native,
      videoUrl: slide.media.url,
      atSeconds: slide.videoCover.time,
      croppedAreaPixels: bake ? slide.crop.croppedAreaPixels : null,
      caption: slide.videoCover.mode === 'frame' && bake ? slide.videoCaption : null,
      photoUrl: slide.videoCover.mode === 'photo' ? slide.videoCover.photo?.url || null : null,
    });
    const [row] = await uploadMedia(
      businessId,
      [new File([blob], 'video-cover.jpg', { type: 'image/jpeg' })],
      { source: 'social_crop' },
    );
    if (!row?.url) throw new Error('Could not save the cover image');
    trackEphemeralMedia(row.id);
    return row.url;
  };

  const handleWriteCaption = async (opts?: { prompt?: string }) => {
    if (!businessId || slides.length === 0) return;
    const userPrompt = String(opts?.prompt || '').trim();
    setWritingCaption(true);
    try {
      // Vision models only accept images: send photos as-is and a still frame
      // for videos (never the .mp4 URL, which the API rejects).
      const imageUrls = slides
        .map((s) => (s.isVideo ? s.posterUrl || '' : s.media.url))
        .filter(Boolean)
        .slice(0, 3);
      const videoNote = hasVideoSlide
        ? `This is a ${postFormat === 'reel' ? 'Reel' : postFormat === 'story' ? 'Story' : 'video post'}; any attached image is a still frame from the video.`
        : null;
      const briefNote = userPrompt
        ? [
            `The business owner gave this specific ${meta.label} caption brief — follow it closely:`,
            `"${userPrompt}"`,
            isInstagram
              ? 'Still write a finished Instagram caption (hook + short body + up to 5 hashtags). Never include URLs. Soft CTA only (DM / link in bio).'
              : 'Still write a finished Facebook post. Prefer Online Booking URL / Website URL from brand guidelines when a link fits.',
          ].join('\n')
        : null;
      const contextNote = [videoNote, briefNote].filter(Boolean).join('\n\n') || null;
      const { data, error } = await supabase.functions.invoke('social-auto-post-action', {
        body: {
          action: 'compose_caption',
          businessId,
          connectionId,
          platform,
          imageUrls,
          ...(contextNote ? { contextNote } : {}),
        },
      });
      if (error || data?.error) {
        const msg =
          data?.error ||
          (await invokeErrorMessage(error, data)) ||
          'Failed to write caption';
        if (data?.code === 'insufficient_tokens' || /token/i.test(String(msg))) {
          toast.error('Not enough AI tokens — purchase more in AI Settings');
        } else {
          toast.error(msg);
        }
        return;
      }
      const content = String(data?.content || '').trim();
      if (!content) {
        toast.error('AI did not return a caption');
        return;
      }
      setPostContent(content);
      setShowCaptionAi(false);
      setCaptionAiMode('menu');
      setCaptionPrompt('');
      toast.success(userPrompt ? 'Caption written from your prompt' : 'Caption written');
    } catch (err) {
      console.error(err);
      toast.error('Failed to write caption');
    } finally {
      setWritingCaption(false);
    }
  };

  if (!open && !isAnimating) return null;

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  const handlePublish = async () => {
    if (!businessId || !connectionId) {
      toast.error(`${meta.label} is not connected`);
      return;
    }
    const content = postContent.trim();
    if (platform === 'linkedin') {
      if (!content && slides.length === 0) {
        toast.error('Write a post or add an image');
        return;
      }
    } else if (!content) {
      toast.error('Write a post first');
      return;
    }
    if (meta.requireImage && slides.length === 0) {
      toast.error('Add at least one image');
      return;
    }

    const mirrorStagedOrPublished = async (args: {
      mode: 'now' | 'schedule';
      content: string;
      mediaIds: string[] | null;
      mediaUrls?: string[] | null;
      googleReviewIds: string[];
      sourceLibraryMediaIds: string[];
      scheduledFor?: string;
      thumbnailUrl?: string | null;
    }): Promise<'skipped' | 'posted' | 'failed'> => {
      if (!alsoPostToOther || !otherConnectionId || !otherPlatform || !otherMeta || hideCrossPost || !businessId) return 'skipped';
      const result = await publishSocialTwin({
        mode: args.mode,
        businessId,
        connectionId: otherConnectionId,
        platform: otherPlatform,
        content: args.content,
        mediaIds: args.mediaIds,
        mediaUrls: args.mediaUrls,
        googleReviewIds: args.googleReviewIds,
        sourceLibraryMediaIds: args.sourceLibraryMediaIds,
        scheduledFor: args.scheduledFor,
        postFormat,
        thumbnailUrl: args.thumbnailUrl,
      });
      if (!result.ok) {
        toast.error(`${otherMeta.label} did not post: ${result.message}`);
        return 'failed';
      }
      return 'posted';
    };

    if (collect) {
      setPublishing(true);
      try {
        let mediaIds: string[] = [];
        let mediaUrls: string[] = [];
        let thumbnailUrl: string | null = null;
        if (slides.length > 0) {
          const baked = await bakeAndUploadSlides();
          mediaIds = baked.mediaIds;
          mediaUrls = baked.mediaUrls;
          thumbnailUrl = await uploadVideoCover();
        }
        const igExtras = isInstagram
          ? toInstagramOptionsPayload(instagramOptions, mediaIds.length === 1)
          : null;
        // Keep all session media — the wizard needs the baked files (and the raw
        // slides for re-editing) until the campaign is finally created.
        sessionCommittedRef.current = true;
        ephemeralMediaIdsRef.current.clear();
        collect.onCollect({
          platform,
          connectionId,
          displayName,
          content,
          slides,
          aspectKey,
          postFormat,
          instagramOptions,
          mediaIds,
          mediaUrls,
          googleReviewIds: slideGoogleReviewIds(),
          sourceLibraryMediaIds: slideSourceLibraryMediaIds(),
          instagramOptionsPayload: (igExtras as Record<string, unknown> | null) || null,
          thumbnailUrl,
          crossPost:
            alsoPostToOther && otherConnectionId && otherPlatform && !hideCrossPost
              ? { platform: otherPlatform, connectionId: otherConnectionId }
              : null,
        });
      } catch (err) {
        console.error(err);
        toast.error(err instanceof Error ? err.message : 'Failed to prepare post');
      } finally {
        setPublishing(false);
      }
      return;
    }

    if (postMode === 'schedule') {
      if (!scheduleLocal) {
        toast.error('Pick a date and time');
        return;
      }
      const when = new Date(scheduleLocal);
      if (Number.isNaN(when.getTime()) || when.getTime() <= Date.now()) {
        toast.error('Schedule time must be in the future');
        return;
      }
      setPublishing(true);
      try {
        let mediaIds: string[] | null = null;
        let thumbnailUrl: string | null = null;
        if (slides.length > 0) {
          const baked = await bakeAndUploadSlides();
          mediaIds = baked.mediaIds;
          thumbnailUrl = await uploadVideoCover();
        }

        const googleReviewIds = slideGoogleReviewIds();
        const sourceLibraryMediaIds = slideSourceLibraryMediaIds();
        const igExtras =
          isInstagram
            ? toInstagramOptionsPayload(instagramOptions, (mediaIds?.length || 0) === 1)
            : null;
        const { data, error } = await supabase.functions.invoke('social-auto-post-action', {
          body: {
            action: 'stage_manual',
            businessId,
            connectionId,
            platform,
            content,
            mediaIds,
            mediaId: mediaIds?.[0] || null,
            googleReviewIds,
            scheduledFor: when.toISOString(),
            ...(sourceLibraryMediaIds.length
              ? { sourceLibraryMediaIds }
              : {}),
            ...(igExtras ? { instagramOptions: igExtras } : {}),
            ...(postFormat !== 'feed' ? { postFormat } : {}),
            ...(thumbnailUrl ? { thumbnailUrl } : {}),
          },
        });
        if (error || data?.error) {
          toast.error(
            data?.error || (await invokeErrorMessage(error, data)) || 'Failed to stage post',
          );
          // Bake outputs stay ephemeral until close / successful retry.
          return;
        }
        const mirrored = await mirrorStagedOrPublished({
          mode: 'schedule',
          content,
          mediaIds,
          googleReviewIds,
          sourceLibraryMediaIds,
          scheduledFor: when.toISOString(),
          thumbnailUrl,
        });
        toast.success(
          mirrored === 'posted'
            ? `Staged for ${meta.label} and ${otherMeta?.label || 'the other account'}`
            : mirrored === 'failed'
              ? `Staged for ${meta.label}`
              : 'Staged for later',
        );
        await finishComposeSuccess(mediaIds || []);
      } catch (err) {
        console.error(err);
        toast.error(err instanceof Error ? err.message : 'Failed to stage post');
      } finally {
        setPublishing(false);
      }
      return;
    }

    setPublishing(true);
    try {
      let mediaUrls: string[] | null = null;
      let mediaIds: string[] | null = null;
      let thumbnailUrl: string | null = null;
      if (slides.length > 0) {
        const baked = await bakeAndUploadSlides();
        mediaUrls = baked.mediaUrls;
        mediaIds = baked.mediaIds;
        thumbnailUrl = await uploadVideoCover();
      }

      const googleReviewIds = slideGoogleReviewIds();
      const sourceLibraryMediaIds = slideSourceLibraryMediaIds();
      const igExtras =
        isInstagram
          ? toInstagramOptionsPayload(instagramOptions, (mediaUrls?.length || 0) === 1)
          : null;
      const { data, error } = await supabase.functions.invoke(meta.functionName, {
        body: {
          action: 'create',
          businessId,
          connectionId,
          content,
          mediaUrls,
          mediaIds,
          mediaUrl: mediaUrls?.[0] || null,
          mediaId: mediaIds?.[0] || null,
          googleReviewIds,
          ...(sourceLibraryMediaIds.length
            ? { sourceLibraryMediaIds }
            : {}),
          ...(igExtras ? { instagramOptions: igExtras } : {}),
          ...(postFormat !== 'feed' ? { postFormat } : {}),
          ...(thumbnailUrl ? { thumbnailUrl } : {}),
        },
      });
      if (error || data?.error) {
        const msg =
          data?.error ||
          (await invokeErrorMessage(error, data)) ||
          'Failed to publish post';
        console.error('[CreateSocialPostModal] publish failed', { error, data, msg });
        toast.error(String(msg).slice(0, 280));
        return;
      }
      const mirrored = await mirrorStagedOrPublished({
        mode: 'now',
        content,
        mediaIds,
        mediaUrls,
        googleReviewIds,
        sourceLibraryMediaIds,
        thumbnailUrl,
      });
      toast.success(
        mirrored === 'posted'
          ? `Posted to ${meta.label} and ${otherMeta?.label || 'the other account'}`
          : `Posted to ${meta.label}`,
      );
      await finishComposeSuccess(mediaIds || []);
    } catch (err) {
      console.error(err);
      toast.error(err instanceof Error ? err.message : 'Failed to publish post');
    } finally {
      setPublishing(false);
    }
  };

  const scheduleDisplay = formatScheduleShort(scheduleLocal);
  const aiTurns = activeSlide?.aiEditTurns || [];
  const originalMedia = activeSlide ? activeSlide.baselineMedia || activeSlide.media : null;
  const aiPromptTrimmed = aiEditPrompt.trim();
  const canSendAiPrompt =
    !editingImage && !publishing && aiPromptTrimmed.length >= 8 && !!activeSlide;

  const collaborators = instagramOptions.collaborators || [];
  const userTags = instagramOptions.userTags || [];

  const toggleIgSection = (section: InstagramExtrasSection) => {
    setIgSection((cur) => (cur === section ? null : section));
  };

  const crossPostOn = alsoPostToOther && !!otherConnectionId && !!otherMeta && !hideCrossPost;
  const bothLabels = otherMeta ? `${meta.label} & ${otherMeta.label}` : meta.label;
  const primaryActionLabel = collect
    ? 'Continue'
    : postMode === 'schedule'
      ? scheduleDisplay
        ? `Stage ${crossPostOn ? bothLabels : 'post'} (${scheduleDisplay})`
        : crossPostOn
          ? `Stage for ${bothLabels}`
          : 'Stage post'
      : crossPostOn
        ? `Publish to ${bothLabels}`
        : meta.publishLabel;

  const detailRowClass =
    'w-full flex items-center justify-between gap-3 px-4 py-3.5 text-left bg-white hover:bg-gray-50 transition-colors disabled:opacity-50';

  return createPortal(
    <>
      <div
        className={`fixed inset-0 transition-opacity duration-200 ${
          open ? 'bg-black/50' : 'bg-black/0'
        }`}
        style={{
          zIndex: 100200,
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget) requestClose();
        }}
        onTransitionEnd={handleTransitionEnd}
      >
        <div
          role="dialog"
          aria-labelledby="create-social-post-title"
          aria-modal="true"
          className={`bg-white transform transition-all duration-200 ${
            isMobile
              ? `fixed left-0 right-0 w-full ${
                  open ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
                }`
              : `fixed inset-y-0 right-0 w-full md:w-[600px] shadow-2xl ${
                  open ? 'translate-x-0' : 'translate-x-full'
                }`
          }`}
          style={{
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
            zIndex: 100210,
            top: 'env(safe-area-inset-top, 0px)',
            bottom: 'env(safe-area-inset-bottom, 0px)',
            ...(isMobile && { left: 0, right: 0 }),
            ...(!isMobile && {
              height:
                'calc(100% - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))',
            }),
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="h-full flex flex-col overflow-hidden relative">
            {/* Header: back / title / next */}
            <div className="flex items-center justify-between px-2 py-2.5 border-b border-gray-200 bg-white shrink-0">
              <button
                type="button"
                onClick={step === 'details' ? goToMedia : requestClose}
                disabled={publishing || writingCaption || editingImage}
                className="h-10 w-10 flex items-center justify-center text-gray-700 hover:text-gray-900 rounded-full hover:bg-gray-100 active:scale-95 transition-all duration-150 touch-manipulation disabled:opacity-50"
                style={{ WebkitTapHighlightColor: 'transparent' }}
                aria-label={step === 'details' ? 'Back' : 'Close'}
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
              <h2
                id="create-social-post-title"
                className="text-[17px] font-bold text-gray-900"
                style={{ letterSpacing: '-0.02em' }}
              >
                New post
              </h2>
              {step === 'media' ? (
                <button
                  type="button"
                  onClick={goToDetails}
                  disabled={!canGoNext}
                  className="h-10 px-3 text-[15px] font-semibold text-blue-600 hover:text-blue-700 rounded-full active:scale-95 transition-all duration-150 disabled:opacity-40 disabled:pointer-events-none"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  Next
                </button>
              ) : (
                <span className="h-10 w-10" aria-hidden />
              )}
            </div>

            <div
              className={`flex-1 overflow-y-auto bg-white ${isMobile ? 'p-4' : 'p-5'} space-y-4`}
              style={{ WebkitOverflowScrolling: 'touch' }}
            >
              {resolving ? (
                <div className="flex items-center justify-center gap-2 py-16 text-sm text-gray-500">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading…
                </div>
              ) : !connectionId ? (
                <p className="text-sm text-gray-500 py-16 text-center">
                  {meta.label} is not connected.
                </p>
              ) : step === 'media' ? (
                <>
                  {/* Account row */}
                  <div className="flex items-center gap-2.5">
                    <span className="h-9 w-9 rounded-full bg-white border border-gray-200 inline-flex items-center justify-center shrink-0 overflow-hidden">
                      <PlatformIcon className="h-9 w-9" />
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {displayName || meta.label}
                      </p>
                      <p className="text-xs text-gray-400">{meta.label}</p>
                    </div>
                  </div>

                  {slides.length === 0 ? (
                    <>
                      {/* Bulk create — standalone entry above the single-post options */}
                      {!showAiCreate && platform !== 'linkedin' ? (
                        <button
                          type="button"
                          onClick={() => setShowBulkCreate(true)}
                          disabled={publishing}
                          className="w-full flex items-start gap-3 px-4 py-4 text-left rounded-2xl border border-violet-200 bg-violet-50/60 shadow-sm hover:bg-violet-100/60 transition-colors disabled:opacity-50"
                        >
                          <span className="mt-0.5 h-10 w-10 rounded-xl bg-violet-600 text-white inline-flex items-center justify-center shrink-0">
                            <CalendarDays className="h-5 w-5" />
                          </span>
                          <span>
                            <span className="block text-[15px] font-semibold text-gray-900">
                              Bulk create with AI
                            </span>
                            <span className="block text-xs text-gray-500 mt-0.5">
                              Pick days on the calendar — AI builds a post for each
                            </span>
                          </span>
                        </button>
                      ) : null}

                      {/* Initial media select */}
                      {!showAiCreate ? (
                        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                          <button
                            type="button"
                            onClick={() => {
                              setShowAddPhotoMenu(false);
                              setShowMediaPicker(true);
                            }}
                            disabled={publishing}
                            className="w-full flex items-start gap-3 px-4 py-4 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 disabled:opacity-50"
                          >
                            <span className="mt-0.5 h-10 w-10 rounded-xl bg-blue-100 text-blue-700 inline-flex items-center justify-center shrink-0">
                              <ImagePlus className="h-5 w-5" />
                            </span>
                            <span>
                              <span className="block text-[15px] font-semibold text-gray-900">
                                Select from Media library
                              </span>
                              <span className="block text-xs text-gray-500 mt-0.5">
                                Use photos you already uploaded
                              </span>
                            </span>
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setShowAddPhotoMenu(false);
                              setShowAiCreate(true);
                            }}
                            disabled={publishing}
                            className="w-full flex items-start gap-3 px-4 py-4 text-left hover:bg-blue-50 transition-colors disabled:opacity-50"
                          >
                            <span className="mt-0.5 h-10 w-10 rounded-xl bg-blue-100 text-blue-700 inline-flex items-center justify-center shrink-0">
                              <Sparkles className="h-5 w-5" />
                            </span>
                            <span>
                              <span className="block text-[15px] font-semibold text-gray-900">
                                Create AI Image
                              </span>
                              <span className="block text-xs text-gray-500 mt-0.5">
                                Generate new visuals or a Google review card
                              </span>
                            </span>
                          </button>
                        </div>
                      ) : null}

                      {showAiCreate && businessId ? (
                        <InstagramAiCreatePanel
                          businessId={businessId}
                          businessName={selectedBusiness?.name || displayName || 'our business'}
                          aspectKey={aspectKey}
                          onAspectChange={handleAspectChange}
                          onClose={() => setShowAiCreate(false)}
                          onCreated={(media, opts) =>
                            addMediaAsSlide(media, { ...opts, ephemeral: true })
                          }
                        />
                      ) : null}

                      {!isInstagram ? (
                        <p className="text-xs text-gray-400 text-center">
                          Facebook posts can be text-only — tap Next to skip photos.
                        </p>
                      ) : null}
                    </>
                  ) : (
                    <>
                      {/* Feed / Reel / Story */}
                      {supportsFormat ? (
                        <div className="flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 p-0.5 self-start">
                          {SOCIAL_POST_FORMATS.map((fmt) => {
                            const Icon =
                              fmt.id === 'reel' ? Clapperboard : fmt.id === 'story' ? RectangleVertical : ImagePlus;
                            return (
                              <button
                                key={fmt.id}
                                type="button"
                                onClick={() => setPostFormat(fmt.id)}
                                disabled={publishing || editingImage}
                                title={fmt.hint}
                                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors disabled:opacity-50 ${
                                  postFormat === fmt.id
                                    ? 'bg-gray-900 text-white'
                                    : 'text-gray-600 hover:text-gray-900'
                                }`}
                                style={{ WebkitTapHighlightColor: 'transparent' }}
                              >
                                <Icon className="h-3.5 w-3.5" />
                                {fmt.label}
                              </button>
                            );
                          })}
                        </div>
                      ) : null}

                      {/* Media preview. Videos use the same frame as photos so the
                          chosen ratio is visible and the clip can be dragged. */}
                      {activeSlide ? (
                        <div className="relative rounded-2xl overflow-hidden bg-black/95 shadow-[0_8px_30px_rgba(0,0,0,0.12)] py-3">
                          <SocialImageCropEditor
                            imageUrl={activeSlide.isVideo ? undefined : mediaPreviewUrl(activeSlide.media.url)}
                            videoUrl={activeSlide.isVideo ? activeSlide.media.url : undefined}
                            aspect={aspect}
                            value={activeSlide.crop}
                            onChange={updateActiveCrop}
                            previewMaxWidth={isMobile ? Math.min(340, window.innerWidth - 64) : 430}
                            previewMaxHeight={isMobile ? 420 : 480}
                            hideZoomSlider
                            ratioLabel={
                              activeSlide.isVideo
                                ? postFormat === 'feed'
                                  ? aspectKey
                                  : '9:16'
                                : undefined
                            }
                            caption={activeSlide.isVideo ? activeSlide.videoCaption : undefined}
                            onCaptionChange={(next) => updateActiveVideoCaption(next)}
                            captionSelected={captionSelected}
                            onCaptionSelectedChange={setCaptionSelected}
                            captionDisabled={publishing}
                          />
                          <button
                            type="button"
                            onClick={() => removeSlide(activeSlideIndex)}
                            disabled={editingImage || publishing}
                            className="absolute top-2.5 right-2.5 z-30 h-9 w-9 inline-flex items-center justify-center rounded-full bg-black/55 text-white hover:bg-black/75 active:scale-95 transition disabled:opacity-50"
                            style={{ WebkitTapHighlightColor: 'transparent' }}
                            aria-label={activeSlide.isVideo ? 'Remove video' : 'Remove photo'}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                          {editingImage && !activeSlide.isVideo ? (
                            <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-black/45 backdrop-blur-[2px]">
                              <div className="h-11 w-11 rounded-full bg-white/15 border border-white/25 inline-flex items-center justify-center">
                                <Loader2 className="h-5 w-5 text-white animate-spin" />
                              </div>
                              <p className="text-[13px] font-medium text-white tracking-tight">
                                Editing image…
                              </p>
                            </div>
                          ) : null}
                        </div>
                      ) : null}

                      {/* Aspect ratio. Feed picks 4:5 / 1:1 / 1.91:1; Reels and Stories are 9:16. */}
                      <div className="flex items-center justify-between gap-2">
                        {postFormat === 'feed' ? (
                        <div className="flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 p-0.5">
                          {IG_ASPECT_OPTIONS.map((opt) => (
                            <button
                              key={opt.id}
                              type="button"
                              onClick={() => handleAspectChange(opt.id)}
                              disabled={editingImage || publishing}
                              className={`rounded-full px-2.5 py-1.5 text-xs font-semibold transition-colors disabled:opacity-50 ${
                                aspectKey === opt.id
                                  ? 'bg-gray-900 text-white'
                                  : 'text-gray-600 hover:text-gray-900'
                              }`}
                              style={{ WebkitTapHighlightColor: 'transparent' }}
                              title={opt.label}
                            >
                              {opt.id}
                            </button>
                          ))}
                        </div>
                        ) : (
                          <div className="flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 p-0.5">
                            <span className="rounded-full bg-gray-900 px-2.5 py-1.5 text-xs font-semibold text-white">
                              9:16
                            </span>
                          </div>
                        )}
                        <span className="text-xs font-semibold text-gray-500 tabular-nums">
                          {activeSlideIndex + 1}/{meta.maxImages}
                        </span>
                        {slides.length > 1 ? (
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => moveSlide(activeSlideIndex, -1)}
                              disabled={activeSlideIndex === 0 || editingImage}
                              className="h-8 w-8 inline-flex items-center justify-center rounded-full border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                              aria-label="Move photo earlier"
                              title="Move photo earlier"
                            >
                              <ChevronLeft className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => moveSlide(activeSlideIndex, 1)}
                              disabled={activeSlideIndex >= slides.length - 1 || editingImage}
                              className="h-8 w-8 inline-flex items-center justify-center rounded-full border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                              aria-label="Move photo later"
                              title="Move photo later"
                            >
                              <ChevronRight className="h-4 w-4" />
                            </button>
                          </div>
                        ) : (
                          <span className="w-[68px]" aria-hidden />
                        )}
                      </div>

                      {activeSlide?.isVideo ? (
                        <p className="text-xs text-gray-400">
                          {hasCaptionText(activeSlide.videoCaption)
                            ? captionSelected
                              ? 'Drag the text to move it · pinch or pull the corner to resize'
                              : 'Tap the text to move or resize it · drag the video to reframe'
                            : 'Drag to reposition · scroll to zoom'}
                        </p>
                      ) : null}

                      {activeSlide?.isVideo && postFormat !== 'story' ? (
                        <VideoCoverControls
                          videoUrl={activeSlide.media.url}
                          aspect={aspect}
                          crop={activeSlide.crop}
                          caption={activeSlide.videoCaption}
                          cover={activeSlide.videoCover}
                          disabled={publishing}
                          onChange={updateActiveVideoCover}
                          onPickPhoto={() => setShowCoverPicker(true)}
                        />
                      ) : null}

                      {activeSlide?.isVideo ? (
                        <div
                          className="space-y-2.5"
                          data-video-caption-ui
                        >
                          <label className="block text-xs font-medium text-gray-500" htmlFor="video-caption">
                            Text on the video
                          </label>
                          <textarea
                            id="video-caption"
                            value={activeSlide.videoCaption?.text || ''}
                            onChange={(e) =>
                              updateActiveVideoCaption({
                                text: e.target.value.slice(0, VIDEO_CAPTION_MAX_CHARS),
                              })
                            }
                            onFocus={() => setCaptionSelected(true)}
                            rows={2}
                            disabled={publishing}
                            placeholder="Caption burned onto the video"
                            className="w-full resize-none rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 outline-none focus:border-gray-400 disabled:opacity-50"
                          />

                          {/* Style presets — each chip previews its own font */}
                          <div className="-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                            {VIDEO_CAPTION_STYLES.map((style) => {
                              const current = activeSlide.videoCaption?.style || 'classic';
                              const selected = current === style.id;
                              const color = activeSlide.videoCaption?.color || '#FFFFFF';
                              const chipBg =
                                style.background === 'color'
                                  ? color
                                  : style.background === 'light'
                                    ? '#F3F4F6'
                                    : '#111827';
                              const chipText =
                                style.fixedTextColor ||
                                (style.background === 'color'
                                  ? color.toLowerCase() === '#ffffff' || color.toLowerCase() === '#ffd60a'
                                    ? '#111111'
                                    : '#FFFFFF'
                                  : style.background === 'none' || style.background === 'dark'
                                    ? color.toLowerCase() === '#111111'
                                      ? '#FFFFFF'
                                      : color
                                    : '#111111');
                              return (
                                <button
                                  key={style.id}
                                  type="button"
                                  onClick={() => updateActiveVideoCaption({ style: style.id })}
                                  disabled={publishing}
                                  title={style.label}
                                  className={`flex h-14 w-[72px] shrink-0 flex-col items-center justify-center gap-1 rounded-xl border-2 transition disabled:opacity-50 ${
                                    selected ? 'border-blue-600' : 'border-transparent hover:border-gray-300'
                                  }`}
                                  style={{
                                    backgroundColor: chipBg,
                                    WebkitTapHighlightColor: 'transparent',
                                  }}
                                >
                                  <span
                                    className="leading-none"
                                    style={{
                                      fontFamily: style.fontFamily,
                                      fontWeight: style.weight,
                                      fontStyle: style.italic ? 'italic' : 'normal',
                                      fontSize: style.id === 'script' ? 15 : 17,
                                      color: chipText,
                                      letterSpacing: style.letterSpacing ? `${style.letterSpacing}em` : undefined,
                                      WebkitTextStroke: style.stroke ? '0.6px #000' : undefined,
                                      textShadow:
                                        style.shadow === 'glow'
                                          ? `0 0 6px ${color}`
                                          : style.shadow === 'soft'
                                            ? '0 1px 3px rgba(0,0,0,0.6)'
                                            : style.shadow === 'hard'
                                              ? '1px 1px 0 rgba(0,0,0,0.85)'
                                              : undefined,
                                    }}
                                  >
                                    {style.uppercase ? 'AA' : 'Aa'}
                                  </span>
                                  <span
                                    className="text-[10px] font-medium leading-none"
                                    style={{ color: chipText, opacity: 0.85 }}
                                  >
                                    {style.label}
                                  </span>
                                </button>
                              );
                            })}
                          </div>

                          {/* Colour swatches */}
                          <div className="flex items-center gap-2">
                            <span className="text-[11px] font-medium text-gray-500">Colour</span>
                            <div className="flex items-center gap-1.5">
                              {VIDEO_CAPTION_COLORS.map((hex) => {
                                const current = (activeSlide.videoCaption?.color || '#FFFFFF').toUpperCase();
                                const selected = current === hex.toUpperCase();
                                const style = captionStyle(activeSlide.videoCaption?.style);
                                return (
                                  <button
                                    key={hex}
                                    type="button"
                                    onClick={() => updateActiveVideoCaption({ color: hex })}
                                    disabled={publishing || !!style.fixedTextColor}
                                    aria-label={`Colour ${hex}`}
                                    className={`h-6 w-6 rounded-full border transition disabled:opacity-30 ${
                                      selected
                                        ? 'ring-2 ring-blue-600 ring-offset-1 border-white'
                                        : 'border-gray-300 hover:scale-105'
                                    }`}
                                    style={{ backgroundColor: hex, WebkitTapHighlightColor: 'transparent' }}
                                  />
                                );
                              })}
                            </div>
                            {hasCaptionText(activeSlide.videoCaption) ? (
                              <button
                                type="button"
                                onClick={() =>
                                  updateActiveVideoCaption({ x: 0.5, y: 0.8, size: 0.045 })
                                }
                                disabled={publishing}
                                className="ml-auto text-[11px] font-semibold text-gray-500 hover:text-gray-900 disabled:opacity-50"
                              >
                                Reset position
                              </button>
                            ) : null}
                          </div>

                          <p className="text-xs text-gray-400">
                            This text is part of the video. Instagram stickers, music, and polls only exist in the Instagram app, so they can’t be added from here.
                          </p>
                        </div>
                      ) : null}

                      {/* Thumbnail strip */}
                      <div className="flex gap-2 overflow-x-auto pb-1">
                        {slides.map((slide, index) => (
                          <button
                            key={slide.key}
                            type="button"
                            onClick={() => setActiveSlideIndex(index)}
                            className={`relative h-14 w-14 shrink-0 rounded-xl overflow-hidden border-2 transition ${
                              index === activeSlideIndex
                                ? 'border-gray-900'
                                : 'border-transparent opacity-80 hover:opacity-100'
                            }`}
                            style={{ WebkitTapHighlightColor: 'transparent' }}
                          >
                            {slide.isVideo ? (
                              slide.posterUrl ? (
                                <img
                                  src={slide.posterUrl}
                                  alt=""
                                  className="h-full w-full object-cover pointer-events-none"
                                />
                              ) : (
                                <span className="flex h-full w-full items-center justify-center bg-gray-900 text-white">
                                  <Clapperboard className="h-4 w-4" />
                                </span>
                              )
                            ) : (
                              <img
                                src={mediaThumbUrl(slide.media.url)}
                                alt=""
                                className="h-full w-full object-cover"
                              />
                            )}
                          </button>
                        ))}
                        {slides.length < meta.maxImages ? (
                          <button
                            type="button"
                            onClick={openAddPhotoMenu}
                            disabled={publishing || editingImage}
                            className="h-14 w-14 shrink-0 rounded-xl border-2 border-dashed border-gray-300 text-gray-400 hover:border-blue-400 hover:text-blue-600 inline-flex items-center justify-center transition disabled:opacity-50"
                            style={{ WebkitTapHighlightColor: 'transparent' }}
                            aria-label="Add photo"
                          >
                            <Plus className="h-5 w-5" />
                          </button>
                        ) : null}
                        {Array.from({
                          length: Math.max(0, 4 - slides.length),
                        }).map((_, i) => (
                          <span
                            key={`ghost-${i}`}
                            className="h-14 w-14 shrink-0 rounded-xl bg-gray-100"
                            aria-hidden
                          />
                        ))}
                      </div>

                      {formatProblems.length > 0 ? (
                        <div className="space-y-1">
                          {formatProblems.map((p, i) => (
                            <p
                              key={i}
                              className={`text-xs ${p.level === 'error' ? 'text-red-600' : 'text-amber-700'}`}
                            >
                              {p.message}
                            </p>
                          ))}
                        </div>
                      ) : formatSpec?.captionIgnored ? (
                        <p className="text-xs text-gray-400">
                          Stories disappear after 24 hours, and the caption isn't shown on the story.
                        </p>
                      ) : null}

                      {showAddPhotoMenu ? (
                        <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                          <button
                            type="button"
                            onClick={() => {
                              setShowAddPhotoMenu(false);
                              setShowMediaPicker(true);
                            }}
                            className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-100"
                          >
                            <ImagePlus className="h-4 w-4 text-blue-700 shrink-0" />
                            <span className="text-sm font-semibold text-gray-900">
                              Select from Media library
                            </span>
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setShowAddPhotoMenu(false);
                              setShowAiCreate(true);
                            }}
                            className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-blue-50 transition-colors"
                          >
                            <Sparkles className="h-4 w-4 text-blue-700 shrink-0" />
                            <span className="text-sm font-semibold text-gray-900">
                              Create AI Image
                            </span>
                          </button>
                        </div>
                      ) : null}

                      {showAiCreate && businessId ? (
                        <InstagramAiCreatePanel
                          businessId={businessId}
                          businessName={selectedBusiness?.name || displayName || 'our business'}
                          aspectKey={aspectKey}
                          onAspectChange={handleAspectChange}
                          onClose={() => setShowAiCreate(false)}
                          onCreated={(media, opts) =>
                            addMediaAsSlide(media, { ...opts, ephemeral: true })
                          }
                        />
                      ) : null}

                      {/* AI edit history — original + every edit, restorable (photos only) */}
                      {activeSlide && !activeSlide.isVideo && originalMedia && aiTurns.length > 0 ? (
                        <div className="rounded-2xl border border-gray-200 bg-[#F7F7F8] p-3 space-y-3 max-h-80 overflow-y-auto">
                          <AiVersionRow
                            media={originalMedia}
                            label="Original"
                            isCurrent={activeSlide.media.id === originalMedia.id}
                            disabled={editingImage || publishing}
                            onRestore={() => restoreSlideVersion(originalMedia)}
                          />
                          {aiTurns.map((turn) => (
                            <div key={turn.id} className="space-y-2">
                              <div className="flex justify-end">
                                <div className="max-w-[85%] rounded-2xl rounded-br-md bg-gray-900 px-3 py-2 text-[13px] leading-snug text-white">
                                  {turn.label}
                                </div>
                              </div>
                              {turn.status === 'pending' ? (
                                <div className="inline-flex items-center gap-1.5 rounded-2xl rounded-bl-md bg-white border border-gray-200 px-3 py-1.5 text-[12px] text-gray-500">
                                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                  Editing image… usually under a minute
                                </div>
                              ) : turn.status === 'error' ? (
                                <div className="inline-flex rounded-2xl rounded-bl-md bg-red-50 border border-red-100 px-3 py-1.5 text-[12px] text-red-700">
                                  {turn.errorMessage || 'Couldn’t apply that edit — try again'}
                                </div>
                              ) : turn.media ? (
                                <AiVersionRow
                                  media={turn.media}
                                  label="Edited"
                                  isCurrent={activeSlide.media.id === turn.media.id}
                                  disabled={editingImage || publishing}
                                  onRestore={() => restoreSlideVersion(turn.media as MediaItem)}
                                />
                              ) : null}
                            </div>
                          ))}
                          <div ref={aiChatEndRef} />
                        </div>
                      ) : null}

                      {/* Describe-a-change AI input — photos only, video is posted as uploaded */}
                      {activeSlide?.isVideo ? null : (
                      <div className="flex items-end gap-1.5 rounded-[22px] border border-gray-200 bg-white pl-3.5 pr-1.5 py-1 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
                        <Sparkles className="h-4 w-4 text-gray-400 shrink-0 mb-2.5" />
                        <textarea
                          value={aiEditPrompt}
                          onChange={(e) => setAiEditPrompt(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              if (canSendAiPrompt) {
                                void handleAiEditImage(aiPromptTrimmed);
                              }
                            }
                          }}
                          rows={1}
                          disabled={editingImage || publishing}
                          placeholder="Describe a change…"
                          className="flex-1 resize-none bg-transparent text-[15px] leading-5 text-gray-900 placeholder:text-gray-400 py-2 max-h-24 outline-none disabled:opacity-50"
                        />
                        <button
                          type="button"
                          onClick={() => void handleAiEditImage(aiPromptTrimmed)}
                          disabled={!canSendAiPrompt}
                          className="h-9 w-9 shrink-0 mb-0.5 inline-flex items-center justify-center rounded-full bg-gray-900 text-white hover:bg-black active:scale-95 transition disabled:opacity-30"
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                          aria-label="Send edit"
                        >
                          {editingImage ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Send className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                      )}
                    </>
                  )}
                </>
              ) : (
                <>
                  {/* Details step */}
                  {/* Media summary row */}
                  <button
                    type="button"
                    onClick={goToMedia}
                    disabled={publishing || writingCaption}
                    className="w-full flex items-center gap-3 rounded-2xl border border-gray-200 bg-white px-3.5 py-3 text-left hover:bg-gray-50 transition-colors disabled:opacity-50"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    {slides[0] && slideThumbUrl(slides[0]) ? (
                      <span className="relative h-11 w-11 shrink-0">
                        <img
                          src={slideThumbUrl(slides[0]) || undefined}
                          alt=""
                          className="h-11 w-11 rounded-lg object-cover"
                        />
                        {slides[0].isVideo ? (
                          <span className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/25">
                            <Clapperboard className="h-4 w-4 text-white drop-shadow" />
                          </span>
                        ) : null}
                      </span>
                    ) : slides[0]?.isVideo ? (
                      <span className="h-11 w-11 rounded-lg bg-gray-900 inline-flex items-center justify-center text-white shrink-0">
                        <Clapperboard className="h-4 w-4" />
                      </span>
                    ) : (
                      <span className="h-11 w-11 rounded-lg bg-gray-100 inline-flex items-center justify-center text-gray-400 shrink-0">
                        <ImagePlus className="h-4 w-4" />
                      </span>
                    )}
                    <span className="flex-1 text-sm font-medium text-gray-900">
                      {slides.length === 0
                        ? 'No media'
                        : hasVideoSlide
                          ? `Video${formatSpec ? ` · ${formatSpec.label.split(' ').slice(1).join(' ') || 'feed'}` : ''}`
                          : `${slides.length} photo${slides.length === 1 ? '' : 's'}`}
                      <span className="text-blue-600 font-semibold">
                        {' '}
                        · {slides.length === 0 ? 'Add' : 'Edit'}
                      </span>
                    </span>
                    <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />
                  </button>

                  {/* Caption */}
                  <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
                    <div className="flex items-center justify-between gap-2 px-4 pt-3">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                        {isInstagram ? 'Caption' : 'Post content'}
                      </p>
                      <button
                        type="button"
                        onClick={() => {
                          if (writingCaption || publishing || slides.length === 0) return;
                          setShowCaptionAi((v) => {
                            const next = !v;
                            if (next) setCaptionAiMode('menu');
                            return next;
                          });
                        }}
                        disabled={
                          writingCaption || publishing || slides.length === 0 || !businessId
                        }
                        className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-blue-600 hover:text-blue-700 disabled:opacity-40"
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        {writingCaption ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Sparkles className="h-3.5 w-3.5" />
                        )}
                        {postContent.trim() ? 'Rewrite with AI' : 'Write with AI'}
                      </button>
                    </div>

                    {showCaptionAi ? (
                      <div className="mx-3 mt-2 rounded-xl border border-blue-200/80 bg-[#EFF6FF] overflow-hidden">
                        {captionAiMode === 'menu' ? (
                          <div className="divide-y divide-blue-100">
                            <button
                              type="button"
                              disabled={writingCaption || publishing}
                              onClick={() => void handleWriteCaption()}
                              className="w-full flex items-start gap-3 px-3.5 py-3 text-left hover:bg-white/70 transition-colors disabled:opacity-50"
                            >
                              <span className="mt-0.5 h-8 w-8 rounded-full bg-blue-100 text-blue-700 inline-flex items-center justify-center shrink-0">
                                <Sparkles className="h-3.5 w-3.5" />
                              </span>
                              <span>
                                <span className="block text-sm font-semibold text-gray-900">
                                  Write caption
                                </span>
                                <span className="block text-xs text-gray-500 mt-0.5">
                                  AI writes from your photos and brand guidelines
                                </span>
                              </span>
                            </button>
                            <button
                              type="button"
                              disabled={writingCaption || publishing}
                              onClick={() => setCaptionAiMode('prompt')}
                              className="w-full flex items-start gap-3 px-3.5 py-3 text-left hover:bg-white/70 transition-colors disabled:opacity-50"
                            >
                              <span className="mt-0.5 h-8 w-8 rounded-full bg-white border border-blue-200 text-blue-700 inline-flex items-center justify-center shrink-0 text-[11px] font-bold">
                                Aa
                              </span>
                              <span>
                                <span className="block text-sm font-semibold text-gray-900">
                                  Prompt to write
                                </span>
                                <span className="block text-xs text-gray-500 mt-0.5">
                                  Tell AI exactly what the caption should say
                                </span>
                              </span>
                            </button>
                          </div>
                        ) : (
                          <div className="p-3 space-y-2.5">
                            <div className="flex items-center justify-between gap-2">
                              <p className="text-xs font-semibold text-blue-900">
                                Prompt to write
                              </p>
                              <button
                                type="button"
                                disabled={writingCaption}
                                onClick={() => {
                                  setCaptionAiMode('menu');
                                  setCaptionPrompt('');
                                }}
                                className="text-xs font-medium text-blue-700 hover:text-blue-900 disabled:opacity-50"
                              >
                                Back
                              </button>
                            </div>
                            <textarea
                              value={captionPrompt}
                              onChange={(e) => setCaptionPrompt(e.target.value)}
                              rows={3}
                              disabled={writingCaption || publishing}
                              placeholder="e.g. Grateful tone, mention ceramic coating, soft CTA to DM us, 3–5 hashtags…"
                              className="w-full text-sm rounded-xl border border-blue-200 bg-white px-3 py-2.5 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 outline-none disabled:opacity-50"
                            />
                            <div className="flex justify-end gap-2">
                              <button
                                type="button"
                                disabled={writingCaption}
                                onClick={() => {
                                  setShowCaptionAi(false);
                                  setCaptionAiMode('menu');
                                  setCaptionPrompt('');
                                }}
                                className="px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 disabled:opacity-50"
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                disabled={
                                  writingCaption ||
                                  publishing ||
                                  captionPrompt.trim().length < 8
                                }
                                onClick={() =>
                                  void handleWriteCaption({ prompt: captionPrompt })
                                }
                                className="inline-flex items-center gap-1.5 rounded-xl bg-blue-600 px-3.5 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                              >
                                {writingCaption ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Sparkles className="h-4 w-4" />
                                )}
                                {writingCaption ? 'Writing…' : 'Write caption'}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : null}

                    <textarea
                      value={postContent}
                      onChange={(e) => setPostContent(e.target.value)}
                      rows={5}
                      maxLength={meta.maxLength}
                      placeholder={meta.placeholder}
                      disabled={writingCaption}
                      className="w-full text-[15px] leading-relaxed px-4 py-2.5 outline-none resize-none disabled:opacity-60 placeholder:text-gray-400"
                    />
                    <p className="px-4 pb-2.5 text-right text-xs text-gray-400 tabular-nums">
                      {postContent.length}/{meta.maxLength}
                    </p>
                  </div>

                  {otherPlatform && otherConnectionId && !hideCrossPost ? (
                    <CrossPostToggle
                      platform={otherPlatform}
                      checked={alsoPostToOther}
                      onChange={setAlsoPostToOther}
                      disabled={publishing || writingCaption || (otherPlatform === 'instagram' && slides.length === 0)}
                      hint={
                        otherPlatform === 'instagram' && slides.length === 0
                          ? 'Add a photo to also post on Instagram'
                          : null
                      }
                    />
                  ) : null}

                  {/* Instagram extras rows */}
                  {isInstagram && connectionId && businessId ? (
                    <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden divide-y divide-gray-100">
                      {(
                        [
                          {
                            id: 'location' as const,
                            label: 'Location',
                            summary:
                              instagramOptions.locationName ||
                              (instagramOptions.locationId ? 'Tagged' : ''),
                          },
                          {
                            id: 'collaborators' as const,
                            label: 'Collaborators',
                            summary: collaborators.map((c) => `@${c}`).join(', '),
                          },
                          {
                            id: 'tags' as const,
                            label: 'Tag people',
                            summary:
                              userTags.length > 0
                                ? `${userTags.length} tagged`
                                : '',
                          },
                        ] as const
                      ).map((row) => (
                        <div key={row.id}>
                          <button
                            type="button"
                            onClick={() => toggleIgSection(row.id)}
                            disabled={publishing || writingCaption}
                            className={detailRowClass}
                            style={{ WebkitTapHighlightColor: 'transparent' }}
                          >
                            <span className="text-[15px] font-medium text-gray-900">
                              {row.label}
                            </span>
                            <span className="flex items-center gap-1.5 min-w-0">
                              {row.summary ? (
                                <span className="text-[13px] text-gray-500 truncate max-w-[180px]">
                                  {row.summary}
                                </span>
                              ) : null}
                              {igSection === row.id ? (
                                <ChevronDown className="h-4 w-4 text-gray-400 shrink-0" />
                              ) : (
                                <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />
                              )}
                            </span>
                          </button>
                          {igSection === row.id ? (
                            <div className="px-4 pb-4">
                              <InstagramPostExtras
                                businessId={businessId}
                                connectionId={connectionId}
                                allowPeopleTags={slides.length === 1}
                                previewUrl={slides[0] ? slideThumbUrl(slides[0]) : null}
                                value={instagramOptions}
                                onChange={setInstagramOptions}
                                disabled={publishing || writingCaption || editingImage}
                                section={row.id}
                              />
                            </div>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  ) : null}

                  {/* Schedule */}
                  {!collect ? (
                    <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
                      <button
                        type="button"
                        onClick={() => setShowScheduleEditor((v) => !v)}
                        disabled={publishing || writingCaption}
                        className={detailRowClass}
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        <span className="text-[15px] font-medium text-gray-900">Schedule</span>
                        <span className="flex items-center gap-1.5">
                          <span className="text-[13px] text-gray-500">
                            {postMode === 'schedule'
                              ? scheduleDisplay || 'Pick a time'
                              : 'Now'}
                          </span>
                          {showScheduleEditor ? (
                            <ChevronDown className="h-4 w-4 text-gray-400 shrink-0" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />
                          )}
                        </span>
                      </button>
                      {showScheduleEditor ? (
                        <div className="px-4 pb-4 space-y-3">
                          <div className="grid grid-cols-2 gap-2">
                            {(
                              [
                                { id: 'now' as const, label: 'Publish now' },
                                { id: 'schedule' as const, label: 'Stage for later' },
                              ] as const
                            ).map((opt) => (
                              <button
                                key={opt.id}
                                type="button"
                                onClick={() => setPostMode(opt.id)}
                                className={`rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors ${
                                  postMode === opt.id
                                    ? 'border-blue-600 bg-blue-50 text-blue-800'
                                    : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                                }`}
                              >
                                {opt.label}
                              </button>
                            ))}
                          </div>
                          {postMode === 'schedule' ? (
                            <input
                              type="datetime-local"
                              value={scheduleLocal}
                              onChange={(e) => setScheduleLocal(e.target.value)}
                              className="w-full h-11 px-3 rounded-lg border border-gray-300 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                            />
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  ) : null}

                  {!collect ? (
                    <p className="text-xs text-gray-400">
                      Timezone: {browserTimezoneLabel()} — scheduled times use this timezone
                      {selectedBusiness?.timezone
                        ? ` · business TZ: ${selectedBusiness.timezone.replace(/_/g, ' ')}`
                        : ''}
                    </p>
                  ) : null}
                </>
              )}
            </div>

            {/* Footer: primary action (details step only) */}
            {step === 'details' && connectionId && !resolving ? (
              <div
                className="px-4 sm:px-5 py-3.5 border-t border-gray-200 bg-white shrink-0"
                style={{ paddingBottom: 'max(14px, env(safe-area-inset-bottom, 0px))' }}
              >
                <button
                  type="button"
                  onClick={() => void handlePublish()}
                  disabled={publishing || writingCaption || editingImage || !canPublish}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-3.5 text-[15px] font-semibold rounded-2xl bg-blue-600 text-white hover:bg-blue-700 active:scale-[0.99] transition disabled:opacity-50"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  {publishing && <Loader2 className="h-4 w-4 animate-spin" />}
                  {primaryActionLabel}
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {businessId ? (
        <>
        <MediaLibraryPicker
          isOpen={showMediaPicker}
          businessId={businessId}
          multiple
          allowVideo={supportsFormat}
          zIndex={100220}
          excludeUsedFor={
            connectionId
              ? { platform, connectionId }
              : null
          }
          initialSelectedUrls={slides.map((s) => s.media.url)}
          onClose={() => setShowMediaPicker(false)}
          onDone={handlePickerDone}
        />
        <MediaLibraryPicker
          isOpen={showCoverPicker}
          businessId={businessId}
          multiple={false}
          allowVideo={false}
          zIndex={100230}
          initialSelectedUrls={
            activeSlide?.videoCover?.mode === 'photo' && activeSlide.videoCover.photo
              ? [activeSlide.videoCover.photo.url]
              : []
          }
          onClose={() => setShowCoverPicker(false)}
          onDone={(selected) => {
            const photo = selected.find((item) => !isVideoMedia(item));
            setShowCoverPicker(false);
            if (!photo) return;
            updateActiveVideoCover({
              mode: 'photo',
              time: activeSlide?.videoCover?.time ?? 0,
              photo,
            });
          }}
        />
        </>
      ) : null}

      {showBulkCreate && businessId && connectionId && platform !== 'linkedin' ? (
        <BulkCreateSocialPostsModal
          platform={platform}
          businessId={businessId}
          connectionId={connectionId}
          onClose={() => setShowBulkCreate(false)}
          onStaged={() => {
            setShowBulkCreate(false);
            void finishComposeSuccess([]);
          }}
        />
      ) : null}
    </>,
    document.body,
  );
}
